/**
 * hype-announcement-bar — Rotating announcement bar web component.
 *
 * Attributes (all strings, passed from Liquid):
 *   announcements      — JSON string array of announcement objects
 *   rotation-interval  — seconds between announcement rotations
 *   placement          — "top" | "bottom"
 *   bg-color           — background CSS color
 *   text-color         — text CSS color
 *   accent-color       — link/CTA CSS color
 *   show-close         — "true" | "false" — show dismiss X button
 *   background-style   — "solid" | "gradient" | "glass"
 *   gradient-end-color — second color for gradient background mode
 *   size               — "small" | "medium" | "large"
 *   animation-style    — "fade" | "slide" | "none"
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { hexToRgba } from './utils.js';

interface Announcement {
  text: string;
  link?: string;
  start?: string;
  end?: string;
}

const DISMISS_KEY = 'hype_bar_dismissed';

function filterByDate(items: Announcement[]): Announcement[] {
  const now = Date.now();
  return items.filter((item) => {
    if (item.start) {
      const start = new Date(item.start).getTime();
      if (!isNaN(start) && now < start) return false;
    }
    if (item.end) {
      const end = new Date(item.end).getTime();
      if (!isNaN(end) && now > end) return false;
    }
    return true;
  });
}

@customElement('hype-announcement-bar')
export class HypeAnnouncementBar extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: inherit;
      font-size: 16px; /* Pin against Dawn's 62.5% rem trick */
      /* Reserve 44px to prevent CLS while the component initialises. */
      min-height: 44px;
    }
    .bar {
      position: fixed;
      left: 0;
      right: 0;
      z-index: 998;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.625em 2.5em 0.625em 1em;
      background: var(--hype-bg, var(--color-background, #008060));
      color: var(--hype-text, var(--color-foreground, #ffffff));
      min-height: 44px;
      box-sizing: border-box;
      text-align: center;
    }
    .bar.top { top: 0; }
    .bar.bottom { bottom: 0; }

    .announcement {
      font-size: 0.875em;
      font-weight: 500;
      line-height: 1.4;
      opacity: 1;
      transform: translateY(0);
      transition: opacity 0.25s ease, transform 0.25s ease;
      /* Contain the vertical slide within the bar */
      overflow: hidden;
    }
    /* Slide up + fade out (outgoing text) */
    .announcement.rotate-out {
      opacity: 0;
      transform: translateY(-50%);
    }
    /* Start position for incoming text: below */
    .announcement.rotate-in-start {
      opacity: 0;
      transform: translateY(50%);
      transition: none;
    }
    /* Slide up + fade in (incoming text) */
    .announcement.rotate-in {
      opacity: 1;
      transform: translateY(0);
    }

    .announcement a {
      color: var(--hype-accent, var(--color-accent, #c9ffc9));
      text-decoration: none;
    }
    .announcement a:hover {
      text-decoration: underline;
    }

    /* ── Bar entry / exit animations ─────────────────────────────── */
    @keyframes hype-bar-slide-in-top {
      from { transform: translateY(-100%); opacity: 0; }
      to   { transform: translateY(0);     opacity: 1; }
    }
    @keyframes hype-bar-slide-out-top {
      from { transform: translateY(0);     opacity: 1; }
      to   { transform: translateY(-100%); opacity: 0; }
    }
    @keyframes hype-bar-slide-in-bottom {
      from { transform: translateY(100%);  opacity: 0; }
      to   { transform: translateY(0);     opacity: 1; }
    }
    @keyframes hype-bar-slide-out-bottom {
      from { transform: translateY(0);     opacity: 1; }
      to   { transform: translateY(100%);  opacity: 0; }
    }
    .bar.top.entering  { animation: hype-bar-slide-in-top    350ms ease-out both; }
    .bar.top.exiting   { animation: hype-bar-slide-out-top   220ms ease-in  both; }
    .bar.bottom.entering { animation: hype-bar-slide-in-bottom  350ms ease-out both; }
    .bar.bottom.exiting  { animation: hype-bar-slide-out-bottom 220ms ease-in  both; }

    /* ── Gradient background mode ─────────────────────────────────── */
    :host([background-style="gradient"]) .bar {
      background: linear-gradient(135deg, var(--hype-bg, #008060), var(--hype-gradient-end, #333333)) !important;
    }

    /* ── Glassmorphism background mode ───────────────────────────── */
    :host([background-style="glass"]) .bar {
      background: var(--hype-bg-glass, rgba(0, 128, 96, 0.7)) !important;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.18);
    }

    /* ── Size variants ────────────────────────────────────────────── */
    :host([size="medium"]) .bar {
      padding: 0.75em 2.75em 0.75em 1.25em;
      min-height: 52px;
    }
    :host([size="medium"]) .announcement {
      font-size: 1em;
    }
    :host([size="large"]) .bar {
      padding: 1em 3em 1em 1.5em;
      min-height: 64px;
    }
    :host([size="large"]) .announcement {
      font-size: 1.125em;
    }

    /* ── No animation style ──────────────────────────────────────── */
    :host([animation-style="none"]) .announcement,
    :host([animation-style="none"]) .announcement.rotate-out,
    :host([animation-style="none"]) .announcement.rotate-in-start,
    :host([animation-style="none"]) .announcement.rotate-in {
      transition: none !important;
      transform: none !important;
      opacity: 1 !important;
    }

    /* ── Fade-only animation style (legacy "fade") ───────────────── */
    :host([animation-style="fade"]) .announcement.rotate-out {
      opacity: 0;
      transform: none;
    }
    :host([animation-style="fade"]) .announcement.rotate-in-start {
      opacity: 0;
      transform: none;
    }
    :host([animation-style="fade"]) .announcement.rotate-in {
      opacity: 1;
      transform: none;
    }

    /* ── Dismiss button ─────────────────────────────────────────── */
    .close-btn {
      position: absolute;
      top: 50%;
      right: 8px;
      transform: translateY(-50%);
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      color: var(--hype-text, var(--color-foreground, #ffffff));
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.2;
      transition: opacity 0.2s ease;
      border-radius: 4px;
      padding: 0;
    }
    /* 44px touch target via pseudo-element */
    .close-btn::before {
      content: '';
      position: absolute;
      width: 44px;
      height: 44px;
    }
    .bar:hover .close-btn {
      opacity: 0.4;
    }
    .close-btn:hover {
      opacity: 0.7;
    }
    .close-btn:focus-visible {
      opacity: 0.7;
      outline: 2px solid var(--hype-accent, var(--color-accent, #c9ffc9));
      outline-offset: 2px;
    }

    @media (max-width: 480px) {
      .bar {
        padding: 0.5em 2.25em 0.5em 0.75em;
      }
      .announcement {
        font-size: 0.8em;
      }
    }

    /* ── Powered by Hype branding ────────────────────────────── */
    .hype-branding {
      position: absolute;
      bottom: 0.15em;
      right: 0.75em;
    }
    .hype-branding a {
      font-size: 0.55em;
      color: var(--hype-text, var(--color-foreground, #fff));
      opacity: 0.3;
      text-decoration: none;
      letter-spacing: 0.02em;
    }
    .hype-branding a:hover {
      opacity: 0.55;
      text-decoration: underline;
    }

    @media (prefers-reduced-motion: reduce) {
      .announcement,
      .announcement.rotate-out,
      .announcement.rotate-in-start,
      .announcement.rotate-in {
        transition: none !important;
        transform: none !important;
      }
      /* Reduced motion: instant crossfade (opacity only, no translate) */
      .announcement.rotate-out {
        opacity: 0 !important;
      }
      .announcement.rotate-in-start {
        opacity: 0 !important;
      }
      .announcement.rotate-in {
        opacity: 1 !important;
      }
      .close-btn {
        transition: none !important;
      }
      .bar.entering,
      .bar.exiting {
        animation: none !important;
        transform: none !important;
        opacity: 1 !important;
      }
      .bar.exiting {
        opacity: 0 !important;
      }
    }
  `;

  @property() announcements = '[]';
  @property({ attribute: 'rotation-interval' }) rotationInterval = '5';
  @property() placement = 'top';
  @property({ attribute: 'bg-color' }) bgColor = '#008060';
  @property({ attribute: 'text-color' }) textColor = '#ffffff';
  @property({ attribute: 'accent-color' }) accentColor = '#c9ffc9';
  @property({ attribute: 'show-close' }) showClose = 'true';
  @property({ attribute: 'background-style' }) backgroundStyle = 'solid';
  @property({ attribute: 'gradient-end-color' }) gradientEndColor = '#333333';
  @property() size = 'small';
  @property({ attribute: 'animation-style' }) animationStyle = 'fade';
  @property({ attribute: 'hide-branding' }) hideBranding = 'false';
  @property({ attribute: 'custom-css' }) customCss = '';

  @state() private _activeIndex = 0;
  @state() private _rotatePhase: '' | 'out' | 'in-start' | 'in' = '';
  @state() private _dismissed = false;
  @state() private _entering = false;
  @state() private _exiting = false;

  private _items: Announcement[] = [];
  private _rotationTimer: ReturnType<typeof setInterval> | null = null;
  private _fadeTimer: ReturnType<typeof setTimeout> | null = null;
  private _handleKeydown: ((e: KeyboardEvent) => void) | null = null;
  private _customStyleEl: HTMLStyleElement | null = null;

  connectedCallback() {
    super.connectedCallback();

    // Check session dismissal.
    if (sessionStorage.getItem(DISMISS_KEY) === 'true') {
      this._dismissed = true;
      return;
    }

    // Parse and filter announcements.
    try {
      const parsed = JSON.parse(this.announcements) as Announcement[];
      this._items = filterByDate(parsed);
    } catch {
      this._items = [];
    }

    if (this._items.length === 0) return;

    // Keyboard dismiss handler.
    this._handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !this._dismissed) {
        this._dismiss();
      }
    };
    document.addEventListener('keydown', this._handleKeydown);

    // Trigger entry animation on first render.
    this._entering = true;
    setTimeout(() => { this._entering = false; }, 350);

    // Start rotation only if multiple announcements.
    if (this._items.length > 1) {
      const intervalMs = Math.max(3, parseInt(this.rotationInterval, 10)) * 1000;
      this._rotationTimer = setInterval(() => this._rotate(), intervalMs);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._rotationTimer !== null) clearInterval(this._rotationTimer);
    if (this._fadeTimer !== null) clearTimeout(this._fadeTimer);
    if (this._handleKeydown !== null) {
      document.removeEventListener('keydown', this._handleKeydown);
      this._handleKeydown = null;
    }
  }

  override updated(changed: Map<string, unknown>) {
    super.updated(changed);
    if (changed.has('customCss')) {
      this._applyCustomCss();
    }
  }

  private _applyCustomCss() {
    if (!this.shadowRoot) return;
    if (this.customCss) {
      if (!this._customStyleEl) {
        this._customStyleEl = document.createElement('style');
        this.shadowRoot.appendChild(this._customStyleEl);
      }
      this._customStyleEl.textContent = this.customCss;
    } else if (this._customStyleEl) {
      this._customStyleEl.remove();
      this._customStyleEl = null;
    }
  }

  private _rotate() {
    // Phase 1: slide current text up + fade out (250ms).
    this._rotatePhase = 'out';
    this._fadeTimer = setTimeout(() => {
      // Phase 2: swap content + set to "below" start position with no transition.
      this._activeIndex = (this._activeIndex + 1) % this._items.length;
      this._rotatePhase = 'in-start';
      // One rAF to ensure the browser paints the start position before transitioning.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Phase 3: slide new text up + fade in (250ms).
          this._rotatePhase = 'in';
          this._fadeTimer = setTimeout(() => {
            this._rotatePhase = '';
          }, 260);
        });
      });
    }, 260);
  }

  private _dismiss() {
    // Trigger exit animation, then hide.
    this._exiting = true;
    setTimeout(() => {
      this._dismissed = true;
      this._exiting = false;
      sessionStorage.setItem(DISMISS_KEY, 'true');
    }, 220);
  }

  render() {
    if (this._dismissed || this._items.length === 0) return nothing;

    this.style.setProperty('--hype-bg', this.bgColor);
    this.style.setProperty('--hype-text', this.textColor);
    this.style.setProperty('--hype-accent', this.accentColor);
    this.style.setProperty('--hype-gradient-end', this.gradientEndColor);
    this.style.setProperty('--hype-bg-glass', hexToRgba(this.bgColor, 0.7));

    const item = this._items[this._activeIndex];
    const content = item.link
      ? html`<a href="${item.link}">${item.text}</a>`
      : html`${item.text}`;

    const rotateClass = this._rotatePhase === 'out' ? 'rotate-out'
      : this._rotatePhase === 'in-start' ? 'rotate-in-start'
      : this._rotatePhase === 'in' ? 'rotate-in'
      : '';

    return html`
      <div class="bar ${this.placement}${this._entering ? ' entering' : ''}${this._exiting ? ' exiting' : ''}">
        <span class="announcement ${rotateClass}">
          ${content}
        </span>
        ${this.showClose === 'true' ? html`
          <button
            class="close-btn"
            aria-label="Dismiss announcements"
            @click=${() => this._dismiss()}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        ` : nothing}
        ${this.hideBranding !== 'true' ? html`
          <span class="hype-branding">
            <a href="https://tehmop.com/hype" target="_blank" rel="noopener noreferrer">Powered by Hype</a>
          </span>
        ` : nothing}
      </div>
    `;
  }
}
